/**
 * Feature permission codes for the application
 * These codes should match the rule codes in the database
 */

// Define all feature permission codes
export const FEATURES = {
  // Menu items
  MENU_DASHBOARD: 'menu_dashboard',
  MENU_REPORTS: 'menu_reports',
  MENU_SETTINGS: 'menu_settings',

  // Views/Pages
  VIEW_REPORTS: 'view_reports',

  // Report features
  REPORT_ADVANCED_ANALYTICS: 'report_advanced_analytics',
  REPORT_FULL_EXPORT: 'report_full_export',

  // Basic features (all plans)
  BASIC_SALES_ANALYTICS: 'essential_sales_analytics',
  BASIC_DASHBOARD: 'basic_dashboard',
  CUSTOM_REPORTS: 'custom_reports',
  BASIC_INVENTORY_TRACKING: 'basic_inventory_tracking',

  // Professional & Enterprise features
  ADVANCED_INVENTORY_TRACKING: 'advanced_inventory_tracking',
  INVENTORY_OPTIMIZATION: 'inventory_optimization',
  REORDER_SUGGESTION: 'reorder_suggestion',
  DEMAND_FORECASTING: 'demand_forecasting',
  PRODUCT_SEGMENTATION: 'product_segment',
  BASIC_SPREADSHEET_INTEGRATION: 'basic_spreadsheet_integration',
  CUSTOM_DASHBOARD: 'custom_dashboard',
  ADD_REPORT_DASHBOARD: 'add_report_dashboard',
  SCHEDULE_MANAGEMENT: 'schedule_management',
  USER_MANAGEMENT: 'user_management',
  EMAIL_TEMPLATES: 'email_templates',
  PREMIUM_REPORTS: 'premium_reports',
  INCOMING_INVENTORY: 'incoming_inventory',
  // Feature codes from FeatureLockedNotify usage
  FAVORITE_REPORTS_COUNT: 'favorite_reports_count',
  SCHEDULE_REPORTS_COUNT: 'schedule_reports_count',
  EXPORT_GOOGLE_SPREADSHEET: 'export_google_spreadsheet',
  MULTI_USER_LOGIN: 'multi_user_login',
  SUPPLIER_DETAIL: 'supplier_detail',
  BUYING_LIST: 'buying_list',
  PAID_MEASURES: 'paid_measures',
  BUNDLE_PRODUCT_MAPPING: 'bundle_product_mapping',
  MULTI_STORE: 'multi_store',
  // Enterprise-only features
  OPERATIONAL_MODE: 'operational_mode',
  AI_ML_DEMAND_FORECASTING: 'ai_ml_demand_forecasting',
  REPLENISHMENT_CENTER: 'replenishment_center',
  BUYING_CALENDAR: 'buying_calendar',
  SUPPLIER_MANAGEMENT: 'supplier_management',
  PURCHASE_ORDERS: 'purchase_orders',
  PO_LINE_ITEM_COLUMNS: 'po_line_item_columns',
  REPLENISH_FROM_WAREHOUSE: 'replenish_from_warehouse',
  ADVANCED_SPREADSHEET_INTEGRATION: 'advanced_spreadsheet_integration',
  QUICKBOOKS_INTEGRATION: 'quickbooks_integration',
  BUYING_TABLE: 'buying_table',
  AUTOMATED_PURCHASE_ORDER: 'automated_purchase_order',
  SHOPIFY_FLOW_INTEGRATION: 'shopify_flow_integration',

  // AI & Analytics features
  INSIGHTS_HUB: 'insights_hub',
  REPORT_BUILDER: 'report_builder',
};

// Define basic features array (Free Plan)
const BASIC_FEATURES = [
  FEATURES.MENU_DASHBOARD,
  FEATURES.MENU_REPORTS,
  FEATURES.VIEW_REPORTS,
  FEATURES.BASIC_SALES_ANALYTICS,
  FEATURES.BASIC_DASHBOARD,
  FEATURES.CUSTOM_REPORTS,
  FEATURES.BASIC_INVENTORY_TRACKING,
];

// Define professional features array
const PROFESSIONAL_FEATURES = [
  ...BASIC_FEATURES,
  FEATURES.REPORT_FULL_EXPORT,
  FEATURES.ADVANCED_INVENTORY_TRACKING,
  FEATURES.INVENTORY_OPTIMIZATION,
  FEATURES.REORDER_SUGGESTION,
  FEATURES.PRODUCT_SEGMENTATION,
  FEATURES.BASIC_SPREADSHEET_INTEGRATION,
  FEATURES.CUSTOM_DASHBOARD,
  FEATURES.ADD_REPORT_DASHBOARD,
  FEATURES.FAVORITE_REPORTS_COUNT,
  FEATURES.SCHEDULE_REPORTS_COUNT,
  FEATURES.EXPORT_GOOGLE_SPREADSHEET,
  FEATURES.MULTI_USER_LOGIN,
  FEATURES.PAID_MEASURES,
  FEATURES.SCHEDULE_MANAGEMENT,
  FEATURES.USER_MANAGEMENT,
  FEATURES.EMAIL_TEMPLATES,
  FEATURES.PREMIUM_REPORTS,
  FEATURES.INCOMING_INVENTORY,
  FEATURES.INSIGHTS_HUB,
  FEATURES.REPORT_BUILDER,
];

// Define enterprise features array
const ENTERPRISE_FEATURES = [
  ...PROFESSIONAL_FEATURES,
  FEATURES.MENU_SETTINGS,
  FEATURES.REPORT_ADVANCED_ANALYTICS,
  FEATURES.AI_ML_DEMAND_FORECASTING,
  FEATURES.REPLENISHMENT_CENTER,
  FEATURES.BUYING_CALENDAR,
  FEATURES.SUPPLIER_MANAGEMENT,
  FEATURES.PURCHASE_ORDERS,
  FEATURES.REPLENISH_FROM_WAREHOUSE,
  FEATURES.ADVANCED_SPREADSHEET_INTEGRATION,
  FEATURES.SUPPLIER_DETAIL,
  FEATURES.BUYING_LIST,
  FEATURES.AUTOMATED_PURCHASE_ORDER,
];

// Feature to package mapping (for reference)
interface PackageFeatures {
  [key: string]: string[];
}

export const PACKAGE_FEATURES: PackageFeatures = {
  basic: BASIC_FEATURES,
  professional: PROFESSIONAL_FEATURES,
  enterprise: ENTERPRISE_FEATURES,
};

// Define measure codes that require the PAID_MEASURES permission
export const PREMIUM_MEASURE_CODES: string[] = [
  // --- Add premium measure codes here ---
  // Example:
  'demand_forecasting',
  'reorder_suggestion',
  'inventory_replenishment',
  'inventory_planning_date',
  'fixed_period_inventory_replenishment',
  'fixed_order_quantity_inventory_replenishment',
  'inventory_replenishment_min_max',
  'inventory_replenishment_min_max_stock_cover_days',
  'inventory_transfer_suggestion',
  'sell_through_rate',
  'inventory_to_sales_ratio',
  'stock_availability',
  'inventory_turrnover',
  'aging_stock',
  'aging_stock_summary',
  'lost_revenue',
  'inventory_movement',
  'historical_inventory',
  'user_management',
  'schedule_management',
  'email_templates',
  'custom_dashboard',
  'add_report_dashboard',
  'schedule_reports_count',
  'favorite_reports_count',
  'export_google_spreadsheet',
  'multi_user_login',
  'basic_spreadsheet_integration',
  'advanced_inventory_tracking',
  'inventory_optimization',
  'product_segment',
  'quickbooks_integration',
  'incoming_inventory',
  'report_full_export',
  'report_advanced_analytics',
  'lost_revenue',
  'inventory_movement',
  'hourly_sync_data',
  'product_metafield',
  'supplier_management',
  'custom_reports',
  'multiple_shopify_store_analytics',
  'bundle_product_mapping',
  'multi_store',
  'replenish_from_warehouse',
  'po_line_item_columns',
  // --- End of premium measure codes ---
];
